/* eslint-disable react/no-deprecated */
import React, { Component, Touch } from 'react';



import { InteractionMethod, gaTracker } from 'aem-libs-react-ts/src/utils/gaTracker';
import { inputId } from 'aem-libs-tests/src/constants/inputId';
import { createTestId } from 'aem-libs-tests/src/utility/createTestId';
import { Maybe } from 'aem-libs-ts/src/types/utility';
import { isFunction } from 'aem-libs-ts/src/utils/common/isFunction';
import { throttle } from 'aem-libs-ts/src/utils/common/throttle';



import * as SliderStyled from './Slider.styled';
import { configureRange } from './slider-utils';
import { KeyAction, RangeType } from './types';


interface SliderState {
  value: number
  currentStep: number
  range: ReturnType<typeof configureRange>
  pressed: boolean
  isFocused: boolean
}

export interface SliderProps {
  id?: string
  name?: string
  className?: string
  children?: React.ReactNode
  value: number
  defaultValue?: number
  disabled?: boolean
  minLabel?: Maybe<string>
  maxLabel?: Maybe<string>
  onChange?: (value: number) => void
  onChangeComplete?: (value: number, interactionMethod?: InteractionMethod) => void
  trackElement?: string
  range: RangeType[]
  enableGaTracking?: boolean
  appVariant?: Maybe<string>
  rf?: string
  testId?: string
  ['aria-label']?: string
}

export class Slider extends Component<SliderProps, SliderState> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private sliderRect: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private slider: any

  constructor(props: SliderProps) {
    super(props)
    this.state = {
      value: 0,
      currentStep: 0,
      range: configureRange(props.range),
      pressed: false,
      isFocused: false
    }
  }

  componentWillMount() {
    this.handleChange = throttle(this.handleChange, 200)
    this.setInitialState(this.props)
  }

  componentDidMount() {
    window.addEventListener('touchmove', this.handleTouchMove)
    window.addEventListener('touchend', this.handleMouseUp)
    window.addEventListener('mousemove', this.handleMouseMove)
    window.addEventListener('mouseup', this.handleMouseUp)
    window.addEventListener('keyup', this.handleKeyPress)
  }

  componentWillUnmount() {
    //  this.handleChange.cancel()
    window.removeEventListener('touchmove', this.handleTouchMove)
    window.removeEventListener('touchend', this.handleMouseUp)
    window.removeEventListener('mousemove', this.handleMouseMove)
    window.removeEventListener('mouseup', this.handleMouseUp)
    window.removeEventListener('keyup', this.handleKeyPress)
  }

  componentWillReceiveProps(nextProps: SliderProps) {
    if (nextProps.range && nextProps.range !== this.props.range) {
      this.setInitialState(nextProps)
    }
    if (nextProps.value !== this.props.value && nextProps.value !== this.state.value) {
      const value = this.state.range.ensureValue(nextProps.value)
      const currentStep = this.state.range.getStepForValue(value)
      this.setState({ value, currentStep })
    }
  }

  setInitialState = (props: SliderProps) => {
    const range = configureRange(props.range)
    const value = range.ensureValue(props.value)
    const currentStep = range.getStepForValue(value)
    this.setState({ value, range, currentStep })
  }

  handleChange = () => {
    const { value } = this.state
    const { onChange } = this.props
    if (onChange) {
      onChange(value)
    }
  }

  handleChangeComplete = () => {
    const { value } = this.state
    const { onChangeComplete } = this.props
    if (onChangeComplete) {
      onChangeComplete(value, 'slider')
    }

    if (this.props.enableGaTracking && this.props.id && this.props.trackElement) {
      gaTracker.withVariant(this.props.appVariant).onInputChange({
        elementID: this.props.id,
        elementName: this.props.trackElement,
        value: value,
        method: 'slider'
      })
    }
  }

  handleKeyPress = (event: KeyboardEvent) => {
    if (this.state.isFocused && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      this.handleChangeComplete()
    }
  }

  handleMouseUp = () => {
    if (this.state.pressed) {
      this.setState({ pressed: false })
      this.handleChangeComplete()
    }
  }

  handleMouseMove = (e: MouseEvent) => {
    if (this.state.pressed) {
      this.handleMove(e)
    }
  }

  handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    this.handlePress()
    this.handleMove(e)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleTouchMove = (e: any) => {
    if (this.state.pressed) {
      e.preventDefault()
      this.handleMouseMove(e.touches[0])
    }
  }

  handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    this.handlePress()
    if (e.touches[0]) {
      this.handleMove(e.touches[0])
    }
  }

  handlePress = () => {
    this.sliderRect = this.slider.getBoundingClientRect()
    this.setState({ pressed: true })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleMove = (e: Touch | React.MouseEvent<HTMLDivElement, MouseEvent> | MouseEvent) => {
    const { clientX } = e
    const { disabled } = this.props
    const { range } = this.state
    const { width, left, right } = this.sliderRect

    if (!clientX || disabled) {
      return
    }

    let position
    if (clientX < left) {
      position = 0
    } else if (clientX > right) {
      position = right - left
    } else {
      position = clientX - left
    }

    const relativePosition = position / width
    const currentStep = Math.round(relativePosition * range.maxStep)
    const value = relativePosition === 1 ? range.maxValue : range.getValueForStep(currentStep)

    if (value !== this.state.value || currentStep !== this.state.currentStep) {
      this.setState({ value, currentStep }, this.handleChange)
    }
  }

  handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!this.state.isFocused) {
      return
    }

    const { minValue, maxValue, maxStep, minStep } = this.state.range
    let newValue = this.state.value
    const step = (maxValue - minValue) / maxStep

    const range = maxValue - minValue
    const stepRange = maxStep - minStep
    const relativeValue = (newValue - minValue) / range
    const currentStep = minStep + relativeValue * stepRange

    const keyActions: Record<KeyAction, () => void> = {
      ArrowLeft: () => (newValue = Math.max(minValue, newValue - step)),
      ArrowDown: () => (newValue = Math.max(minValue, newValue - step)),
      ArrowRight: () => (newValue = Math.min(maxValue, newValue + step)),
      ArrowUp: () => (newValue = Math.min(maxValue, newValue + step)),
      Home: () => (newValue = minValue),
      End: () => (newValue = maxValue)
    }

    if (keyActions[event?.key as KeyAction]) {
      keyActions[event?.key as KeyAction]()
      this.setState({ value: newValue, currentStep }, this.handleChange)
      event.preventDefault()
    }
  }

  handleFocus = () => {
    this.setState({ isFocused: true })
  }

  handleBlur = () => {
    this.setState({ isFocused: false })
  }

  render() {
    const { id, children, className, minLabel, maxLabel, testId } = this.props
    const { value, range, currentStep } = this.state
    const offset = (currentStep / range.maxStep) * 100
    const offsetStyle = { left: `${offset}%` }
    const progressStyle = { width: `${offset}%` }
    const usedTestId = createTestId(testId ?? 'slider', inputId.slider)

    return (
      <SliderStyled.Container
        className={className}
        onMouseDown={this.handleMouseDown}
        onKeyDown={this.handleKeyDown}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        ref={(node: HTMLDivElement) => {
          this.slider = node
        }}
        data-testid={usedTestId}
        tabIndex={-1}
      >
        <SliderStyled.Track />
        <SliderStyled.Progress style={progressStyle} />
        <SliderStyled.Handle
          onTouchStart={this.handleTouchStart}
          onMouseDown={this.handleMouseDown}
          style={offsetStyle}
        >
          <SliderStyled.Thumb
            role='slider'
            aria-valuemin={range.minValue}
            aria-valuemax={range.maxValue}
            aria-valuenow={value}
            aria-orientation='horizontal'
            aria-labelledby={`${id}-label`}
            tabIndex={0}
            id='pointer'
            data-testid={usedTestId + '-thumb'}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
          />
          {isFunction(children) ? children(value) : children}
        </SliderStyled.Handle>
        <SliderStyled.Labels>
          <span data-testid={usedTestId + '-left-label'} data-label={'left'}>
            {minLabel}
          </span>
          <span data-testid={usedTestId + '-right-label'} data-label={'right'}>
            {maxLabel}
          </span>
        </SliderStyled.Labels>
      </SliderStyled.Container>
    )
  }
}
